<script setup lang="ts">
import {useModalStore} from '../../store/modal';
import {computed, onBeforeUnmount, ref, watch} from "vue";

const modalStore = useModalStore()
const title = computed(() => modalStore.props?.type === 'document' ? 'Attach document.' : 'Attach image or video.')
const previewUrl = ref<string | null>(null);
const MAX_SIZE = 5 * 1024 * 1024;

const isImage = computed(() => modalStore.props.attachment?.type.startsWith('image/') ?? false);
const isVideo = computed(() => modalStore.props.attachment?.type.startsWith('video/') ?? false);
const showPreview = computed(() => isImage.value || isVideo.value);
const handleFileChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const selected = input.files?.[0];

    if (!selected) return;

    if (selected.size > MAX_SIZE) {
        // TODO: show UI message instead of alert
        alert("File is too large (max 5MB).");
        input.value = "";
        return;
    }

    console.log({selected})
    modalStore.props.attachment = selected;
};

watch(() => modalStore.props.attachment, (newFile) => {
    // Clean up previous preview URL
    if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
        previewUrl.value = null;
    }

    // Create new preview URL for images and videos
    if (newFile && (newFile.type.startsWith('image/') || newFile.type.startsWith('video/'))) {
        previewUrl.value = URL.createObjectURL(newFile);
    }
}, {immediate: true});

onBeforeUnmount(() => {
    modalStore.props.attachment = null;

    if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
    }
});
</script>
<template>
    <div class="message-attachment-input">
        <div class="message-attachment-input-header">
            <p>{{ title }}</p>
            <button @click.prevent="modalStore.close()">
                <ah-icon name="cross-default"/>
            </button>
        </div>

        <!--TODO: add drag and drop functionality-->
        <label class="message-attachment-input-drag" for="message-attachment" v-if="!modalStore.props.attachment">
            <input type="file" name="message-attachment" id="message-attachment"
                   :accept="modalStore.props?.type === 'document' ? 'application/pdf, .doc,.docx' : 'image/*, video/*'"
                   @change="handleFileChange"
            >
            <span>Drop file here or click to upload.</span>
            <span>Max  •  5MB</span>
        </label>

        <div :class="`message-attachment-input-preview --${modalStore.props?.type}`" v-else>
            <div class="message-attachment-input-preview-media" v-if="showPreview">
                <img v-if="isImage && previewUrl" :src="previewUrl" alt="Preview"/>
                <video v-if="isVideo && previewUrl" :src="previewUrl" controls></video>
            </div>

            <div class="message-attachment-input-preview-details">
                <ah-icon :name="modalStore.props?.type === 'document' ? 'paper-file' : 'media-image'"
                         color="var(--color-tiffany-dark)"/>
                <div class="message-attachment-input-preview-details-information">
                    <p>{{ modalStore.props.attachment.name }}</p>
                    <p>{{ modalStore.props.attachment.type.split('/')[1].toUpperCase() }} •
                        {{ (modalStore.props.attachment.size / 1024 / 1024).toFixed(2) }}MB</p>
                </div>

                <brand-button as="button" variant="small" @click="modalStore.props.attachment = null">
                    Remove
                </brand-button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.message-attachment-input {
    width: 65rem;
    min-height: 17.6rem;
    background: var(--color-white);
    box-shadow: 0 0 2.4rem 0 rgba(0, 0, 0, 0.40);
    border-radius: 2.4rem;
    position: absolute !important;
    bottom: calc(100% + 2rem);
    left: 2rem;
    padding: 2rem;

    &-header {
        padding: 0 2rem 2rem;
        display: flex;
        align-items: center;
        justify-content: space-between;

        p {
            @include font(1.6, 1.6, black);
            color: var(--color-thunder);
        }

        button {
            width: 3.6rem;
            height: 3.6rem;
            background: none;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: var(--color-white);

            .icon {
                fill: var(--color-hazy);
            }

            &:hover {
                background: var(--color-raspberry);

                .icon {
                    fill: var(--color-white);
                }
            }
        }
    }

    &-drag {
        width: 100%;
        padding: 2rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        border-radius: 1.4rem;
        border: .2rem dashed #CED9E5;

        input {
            display: none;
        }

        span {
            @include font(1.4, 1.4, black);
            color: var(--color-grey-thunder);

            &:last-child {
                @include font(1.2, 1.2, book);
            }
        }

        &:hover {
            background: rgba(206, 217, 229, 0.2);
        }
    }

    &-preview {
        display: flex;
        gap: 2rem;
        padding: 2rem;
        border-radius: 2.4rem;
        background: var(--color-mist);

        &-media {
            width: 32.1rem;
            height: 20rem;
            border-radius: 1.4rem;
            overflow: hidden;
            background: var(--color-grey-dew);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;

            img, video {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        &-details {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 2rem;
            flex: 1;

            &-information {
                display: flex;
                flex-direction: column;
                gap: .6rem;
                align-items: center;

                p {
                    @include font(1.4, 1.4, black);
                    color: var(--color-grey-thunder);
                    text-align: center;

                    &:last-child {
                        @include font(1.2, 1.2, book);
                    }
                }
            }

            button {
                width: 13.2rem;
            }
        }

        &.--document {
            .message-attachment-input-preview-details {
                display: grid;
                grid-template-columns: 2.4rem 1fr 13.1rem;

                &-information {
                    align-items: start;

                    p {
                        text-align: left;
                    }
                }
            }
        }
    }

    @include dark() {
        background: var(--color-darth-maul);

        &-header {
            p {
                color: var(--color-white);
            }

            button {
                background: var(--color-darth-maul);

                .icon {
                    fill: var(--color-dew);
                }

                &:hover {
                    background: var(--color-raspberry);
                }
            }
        }

        &-drag {
            background: var(--color-white-05);
            border-color: var(--color-white-20);

            span {
                color: var(--color-white);
            }
        }

        &-preview {
            background: var(--color-darth-sidious);

            &-details {
                &-information {
                    p {
                        color: var(--color-white);
                    }
                }

                button {
                    &:hover {
                        background: var(--color-dark-orange);
                    }
                }
            }
        }
    }
}
</style>
